// src/components/pipelines/sheets/WhileInStageSection.jsx
import React, { useState } from 'react';
import { Clock, Plus, Trash2 } from 'lucide-react';
import { Button } from '../../ui/button.tsx';
import { Badge } from '../../ui/badge.tsx';
import { Card } from '../../ui/card.tsx';
import { usePipelines } from '../../../hooks/usePipelines';
import RuleBuilder from './RuleBuilder';

/**
 * WhileInStageSection - Configures advance rules (what moves customer to next stage)
 *
 * Each rule creates an AutomationRule with trigger_type='time_based', 'conditional', or 'system_event'
 */
const WhileInStageSection = ({ stage, rules, isLoading }) => {
  const [isAddingRule, setIsAddingRule] = useState(false);
  const { createRule, deleteRule, stages } = usePipelines();

  // Get pipeline stages for next stage selection
  const pipelineStages = stages.filter(s => s.pipeline_id === stage.pipeline_id);

  const handleAddRule = (ruleData) => {
    createRule(stage.id, ruleData);
    setIsAddingRule(false);
  };

  const handleDeleteRule = (ruleId) => {
    if (window.confirm('Are you sure you want to delete this advance rule?')) {
      deleteRule(ruleId);
    }
  };

  const getTriggerTypeLabel = (triggerType) => {
    const labels = {
      'time_based': 'Time-based',
      'system_event': 'Event-based',
      'conditional': 'Conditional'
    };
    return labels[triggerType] || triggerType;
  };

  const getTriggerTypeColor = (triggerType) => {
    const colors = {
      'time_based': 'blue',
      'system_event': 'purple',
      'conditional': 'orange'
    };
    return colors[triggerType] || 'gray';
  };

  return (
    <div className="space-y-2">
      {/* Section Header */}
      <div className="w-full flex items-center justify-between p-2 bg-blue-500/10 border border-blue-500/20 rounded-md">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-blue-400" />
          <h3 className="text-xs font-medium text-blue-100">Rules</h3>
        </div>
        <Badge variant="outline" className="text-xs border-blue-500/50 text-blue-300 h-4 px-1">
          {rules.length}
        </Badge>
      </div>

      <div className="space-y-3">
          {/* Existing Rules */}
          {rules.length > 0 && (
            <div className="space-y-2 pl-3">
              {rules.map((rule) => {
                const nextStage = pipelineStages.find(s => s.id === rule.next_stage_id);
                const color = getTriggerTypeColor(rule.trigger_type);

                return (
                  <Card key={rule.id} className="p-3 bg-slate-800/50 border-slate-700/50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-sm font-medium text-slate-200">{rule.name}</h4>
                          <Badge
                            variant="outline"
                            className={`text-xs border-${color}-500/50 text-${color}-400`}
                          >
                            {getTriggerTypeLabel(rule.trigger_type)}
                          </Badge>
                          {!rule.is_active && (
                            <Badge variant="outline" className="text-xs border-yellow-500/50 text-yellow-400">
                              Inactive
                            </Badge>
                          )}
                        </div>

                        {/* Rule Details */}
                        <div className="space-y-1 text-xs text-slate-400">
                          {rule.trigger_type === 'system_event' && rule.trigger_event && (
                            <div>• Triggered by: {rule.trigger_event.replace(/_/g, ' ')}</div>
                          )}
                          {rule.conditions && rule.conditions.length > 0 && (
                            <div>
                              • Conditions: {rule.conditions.map(c =>
                                `${c.field} ${c.operator} ${c.value}`
                              ).join(', ')}
                            </div>
                          )}
                          {nextStage && (
                            <div>• Move to: {nextStage.name}</div>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-1 ml-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0 text-slate-400 hover:text-red-400"
                          onClick={() => handleDeleteRule(rule.id)}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Add Rule Button */}
          {!isAddingRule && (
            <div className="pl-3">
              <Button
                variant="outline"
                size="sm"
                className="w-full h-7 border-blue-500/30 text-blue-300 hover:bg-blue-500/10 hover:text-blue-200 text-xs"
                onClick={() => setIsAddingRule(true)}
              >
                <Plus className="w-3 h-3 mr-1" />
                Add
              </Button>
            </div>
          )}

          {/* Rule Builder */}
          {isAddingRule && (
            <div className="pl-3">
              <RuleBuilder
                stage={stage}
                pipelineStages={pipelineStages}
                onSave={handleAddRule}
                onCancel={() => setIsAddingRule(false)}
              />
            </div>
          )}

          {/* Loading State */}
          {isLoading && rules.length === 0 && (
            <div className="pl-3 text-xs text-slate-500 italic">
              Loading...
            </div>
          )}
      </div>
    </div>
  );
};

export default WhileInStageSection;
